<template>
  <v-data-table
    :item-key="itemKey"
    :headers="tableHeaders"
    :items="filteredItems"
    :height="height"
    :mobile-breakpoint="0"
    class="card-shadow rounded-0"
    :item-class="classItem"
    :hide-default-footer="!paginated"
    :disable-pagination="!paginated"
    :custom-sort="customSort"
    :show-select="!hideSelect"
    :loading="loading"
    :value="selectedReminders"
    sort-by="date"
    sort-desc
    dense
    @input="onSelectReminder"
    @item-selected="emitSelection"
  >
    <template #[`header.data-table-select`]="{ props, on }">
      <div>{{ isDelete ? 'Eliminar' : 'Ir a Carrito' }}</div>
      <v-simple-checkbox
        v-if="isDelete"
        v-model="selectAll"
        :indeterminate="props.indeterminate"
        @input="handleCustomSelect(item, $event)"
        v-on="on"
      />
    </template>

    <template #[`item.data-table-select`]="{ item, isSelected, select }">
      <v-simple-checkbox
        :value="isSelected"
        :class="[
          'reminder-item-checkbox',
          {
            'cursor-not-allowed': !isReminderSelectable(item),
            'pointer-events-none': loading,
          },
        ]"
        :disabled="!isReminderSelectable(item)"
        @input="select($event)"
      />
    </template>

    <template #[`item.order`]="{ item }">
      <div class="d-flex align-center">
        <div class="mr-2">{{ item.order }}</div>
        <svg-icon v-if="item.isReel" name="reel-icon" label="Carrete" />
        <order-type-tooltip v-else :value="item.typeDocument" />
      </div>
    </template>

    <template #[`item.requiredQty`]="{ item }">
      <pill-status :status="item.status" class="my-1">
        {{ formatNumber(item.requiredQty, '') }}
      </pill-status>
    </template>

    <template #[`item.customer`]="{ item }">
      <div>{{ item.customer?.code }} - {{ item.customer?.name }}</div>
    </template>

    <template #[`item.discountPrice`]="{ item }">
      <div>$ {{ formatNumber(item.discountPrice, '') }}</div>
    </template>

    <template #[`item.listPrice`]="{ item }">
      <div>$ {{ formatNumber(item.listPrice, '') }}</div>
    </template>

    <template #[`item.type`]="{ item }">
      <div class="d-flex align-center">
        <span v-if="item.type === 'PR'" class="blue--text text--darken-2">
          PROGRAMADO
        </span>
        <span v-else> BACKORDER </span>
      </div>
    </template>
    <template #[`item.materialName`]="{ item }">
      <div>
        <div class="mr-2">{{ item.materialName }}</div>
        <special-discount-icon :discount="item" />
      </div>
    </template>
  </v-data-table>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import PillStatus from '../PillStatus.vue';
  import SvgIcon from '../icons/SvgIcon.vue';
  import numbersFormats from '@/mixins/numbersFormats';
  import OrderTypeTooltip from '@/components/tooltips/OrderTypeTooltip.vue';

  import { compareDatesWithStringFormat } from '@/utilities/date-helpers';
  import SpecialDiscountIcon from '@/views/shopping_cart_new/components/SpecialDiscountIcon.vue';

  export default {
    name: 'RemindersTable',
    components: { PillStatus, SvgIcon, OrderTypeTooltip, SpecialDiscountIcon },
    mixins: [numbersFormats],
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      items: {
        type: Array,
        required: true,
      },
      search: {
        type: String,
        default: '',
      },
      filter: {
        type: String,
        default: null,
      },
      itemKey: {
        type: String,
        default: 'uniqueId',
      },
      height: {
        type: String,
        default: '320px',
      },
      hiddenHeaders: {
        type: Array,
        default: () => [],
      },
      hideSelect: Boolean,
      loading: Boolean,
      isDelete: Boolean,
      paginated: Boolean,
    },
    data: () => ({
      headers: [
        { text: 'Número de pedido', sortable: false, value: 'order' },
        { text: 'Tipo', value: 'type' },
        { text: 'Fecha', value: 'date' },
        { text: 'Fecha Programado', value: 'dateProgrammed' },
        { text: 'Gerente Regional', align: 'start', value: 'regionalManager' },
        { text: 'Gerente', align: 'start', value: 'manager' },
        { text: 'Vendedor', align: 'start', value: 'seller' },
        { text: 'Cliente', align: 'start', value: 'customer' },
        { text: 'Ord. Compra', value: 'orderConcept' },
        { text: 'Código', value: 'materialId' },
        { text: 'Nombre', sortable: false, value: 'materialName' },
        { text: 'Precio Unitario', sortable: false, value: 'listPrice' },
        {
          text: 'Precio c/ Descuento',
          sortable: false,
          value: 'discountPrice',
        },
        { text: 'Unidades', sortable: false, value: 'requiredQty' },
        { text: '', value: 'data-table-select' },
      ],
      searchableFields: [
        'id',
        'date',
        'dateProgrammed',
        'regionalManager',
        'manager',
        'seller',
        'customer.code',
        'customer.name',
        'orderConcept',
        'materialId',
        'materialName',
      ],
      selectAll: false,
      tableKey: 0,
    }),
    computed: {
      ...mapGetters('reminders', [
        'isReminderSelectable',
        'isReminderCompatible',
        'isReminderWithErrors',
        'selectedReminders',
      ]),
      filteredItems() {
        let list = [...this.items];

        if (this.filter) {
          list = list.filter((item) => {
            // Filter PROGRAMMED backorders
            if (this.filter === 'PR') {
              return item.type === this.filter;
            }

            // The backorder expiration limit is BACKORDER_EXPIRY_DAYS (60).
            // Items that are within 5 days of that limit are considered "expiring soon" and included in this filter.

            return item.status === this.filter;
          });
        }

        if (this.search) {
          list = list.filter((item) =>
            this.matchesSearch(item, this.search, this.searchableFields)
          );
        }

        return list;
      },
      tableHeaders() {
        const hiddenHeadersList = [
          ...(this.hideSelect ? ['data-table-select'] : []),
          ...this.hiddenHeaders,
        ];

        return this.headers.filter(
          (header) => !hiddenHeadersList.includes(header.value)
        );
      },
    },
    methods: {
      ...mapActions('reminders', [
        'updateSelectedReminders',
        'selectOrderGroup',
      ]),

      emitSelection({ item, value }) {
        if (this.isDelete) {
          return;
        }

        this.selectOrderGroup(item);
        this.$emit('itemSelected', { item, value });
      },

      onSelectReminder(reminders) {
        if (this.isDelete) {
          this.updateSelectedReminders(reminders);
          return;
        }
      },
      classItem(item) {
        if (this.isDelete || this.hideSelect) return '';

        const classes = [];

        if (!this.isReminderSelectable(item))
          classes.push('cursor-not-allowed');
        if (!this.isReminderCompatible(item) || this.isReminderWithErrors(item))
          classes.push('selection-not-allowed');

        return classes.join(' ');
      },
      matchesSearch(item, search, fields) {
        if (!search) return true;
        const text = search.toLowerCase();

        for (const path of fields) {
          // Nested Values
          const value = path.split('.').reduce((obj, key) => obj?.[key], item);

          if (value != null) {
            const valStr = value.toString().toLowerCase();
            if (valStr.includes(text)) {
              return true;
            }
          }
        }

        return false;
      },
      customSort(items, index, isDesc) {
        if (!index || !index.length || isDesc[0] === undefined) {
          return items;
        }

        const key = index[0];
        const desc = isDesc[0];

        items.sort((a, b) => {
          if (key === 'customer') {
            const nameA = a.customer?.name || '';
            const nameB = b.customer?.name || '';
            return desc
              ? nameB.localeCompare(nameA)
              : nameA.localeCompare(nameB);
          }

          // Sorts dates using a custom comparator since the date strings are not in ISO format
          // and cannot be reliably compared by default.
          if (key === 'date' || key === 'dateProgrammed') {
            const result = compareDatesWithStringFormat(a[key], b[key]);
            return desc ? -result : result;
          }

          const valueA = a[key];
          const valueB = b[key];

          if (valueA === valueB) return 0;

          if (desc) {
            return valueB < valueA ? -1 : 1;
          } else {
            return valueA < valueB ? -1 : 1;
          }
        });

        return items;
      },
      handleCustomSelect(item, isChecking) {
        if (isChecking) {
          const relatedItems = this.items.filter(
            (i) =>
              i.order === item.order &&
              i.status !== 'GREY' &&
              Number(i.isReel) !== 1 &&
              !this.isSelectable(i)
          );

          const newSelection = [...this.stockItems];
          relatedItems.forEach((ri) => {
            if (!newSelection.find((s) => s.id === ri.id)) {
              newSelection.push(ri);
            }
          });

          this.stockItems = newSelection;
        } else {
          this.stockItems = this.stockItems.filter(
            (s) => s.order !== item.order
          );
        }
      },
    },
  };
</script>

<style scoped>
  .v-data-table >>> .v-data-table-header {
    box-shadow: inset 0px 3px 6px #00000029, 0px 3px 6px #00000029 !important;
    background-color: rgb(242, 242, 242);
  }

  .v-data-table >>> .v-data-table-header > tr > th {
    color: black !important;
  }

  .v-data-table >>> .v-data-table-header > tr > th {
    border-bottom: 2px solid rgb(112, 112, 112) !important;
  }

  .pointer-events-none {
    pointer-events: none;
  }

  ::v-deep(
      .reminder-item-checkbox.v-simple-checkbox--disabled
        .theme--light.v-icon.v-icon--disabled
    ) {
    color: #f44336 !important;
    cursor: not-allowed;
    opacity: 0.3 !important;
  }
</style>

<style>
  .selection-not-allowed,
  .selection-not-allowed:hover {
    background-color: rgb(243, 220, 220) !important;
  }
</style>
